import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router';

import { useChangeUnverifiedEmail, useResendEmail } from 'hooks/useEmailVerification';
import { Button, TextField } from 'ui';

import classes from './register.module.scss';

export const ConfirmEmail: FC = () => {
  const [searchParams] = useSearchParams();
  const queryEmail = (searchParams.get('email') || '').trim();
  const storedEmail = (localStorage.getItem('registration_email') || '').trim();
  const [email, setEmail] = useState(queryEmail || storedEmail);
  const [password, setPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showChange, setShowChange] = useState(false);
  const { mutate: resendEmail, isPending } = useResendEmail();
  const changeEmail = useChangeUnverifiedEmail();
  const [timeLeft, setTimeLeft] = useState(queryEmail || storedEmail ? 60 : 0);

  useEffect(() => {
    if (queryEmail) {
      localStorage.setItem('registration_email', queryEmail);
    }
  }, [queryEmail]);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timerId = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(timerId);
  }, [timeLeft]);

  const maskEmail = (mail: string) => {
    const [name, domain] = mail.split('@');
    if (!domain) return mail;
    return `${name.charAt(0)}*******@${domain}`;
  };

  const canResend = useMemo(
    () => Boolean(email.trim() && email.includes('@') && timeLeft <= 0 && !isPending),
    [email, timeLeft, isPending],
  );

  const handleResend = () => {
    const next = email.trim();
    if (!next.includes('@')) return;
    localStorage.setItem('registration_email', next);
    resendEmail(next, {
      onSuccess: () => setTimeLeft(60),
    });
  };

  const handleChangeEmail = () => {
    if (!email.trim() || !password || !newEmail.trim()) return;
    changeEmail.mutate(
      { email: email.trim(), password, new_email: newEmail.trim() },
      {
        onSuccess: (data) => {
          if (data.email) setEmail(data.email);
          setPassword('');
          setNewEmail('');
          setShowChange(false);
          setTimeLeft(60);
        },
      },
    );
  };

  return (
    <section className={classes.wrapper}>
      <main className={`${classes.container} ${classes.centeredContainer}`}>
        <div className={classes.topSection}>
          <div className={classes.badgeIcon}>⭐</div>
          <h1 className={classes.title}>Осталось пару шагов!</h1>

          <div className={classes.infoBox}>
            <h3 className={classes.infoBoxTitle}> Подтверждение email</h3>
            <p className={classes.infoBoxText}>
              Мы отправили письмо на
              <br />
              <strong>{email.includes('@') ? maskEmail(email) : 'указанную почту'}</strong>
            </p>
            <p className={classes.infoBoxTextWithMargin}>
              Перейдите по ссылке в письме,
              <br />
              чтобы подтвердить аккаунт.
            </p>
          </div>
        </div>

        <div className={classes.bottomSection}>
          {!storedEmail && !queryEmail ? (
            <div style={{ marginBottom: 16 }}>
              <TextField
                className='fig-auth-field'
                shape='brick'
                placeholder='Email, на который регистрировались'
                size='lg'
                name='email'
                value={email}
                onChange={(val) => setEmail(val)}
              />
            </div>
          ) : null}

          <p className={classes.timerText}>
            {timeLeft > 0
              ? `Если письмо не пришло, можем отправить его повторно через 0:${timeLeft < 10 ? '0' : ''}${timeLeft}`
              : 'Можно запросить письмо еще раз - даже с другого устройства'}
          </p>

          <Button
            variant='filled'
            shape='brick'
            color='secondary'
            widthFullSize
            loading={isPending}
            disabled={!canResend}
            onClick={handleResend}
            className={classes.submitButton}
          >
            Отправить письмо повторно
          </Button>

          <Button
            type='button'
            variant='clear'
            color='primary'
            size='clear'
            className={classes.link}
            style={{ marginTop: 16 }}
            onClick={() => setShowChange((v) => !v)}
          >
            Не тот адрес? Исправить email
          </Button>

          {showChange ? (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <TextField
                className='fig-auth-field'
                shape='brick'
                placeholder='Текущий email'
                size='lg'
                name='current-email'
                value={email}
                onChange={(val) => setEmail(val)}
              />
              <TextField
                className='fig-auth-field'
                shape='brick'
                placeholder='Пароль аккаунта'
                size='lg'
                type='password'
                name='password'
                value={password}
                onChange={(val) => setPassword(val)}
              />
              <TextField
                className='fig-auth-field'
                shape='brick'
                placeholder='Новый email'
                size='lg'
                name='new-email'
                value={newEmail}
                onChange={(val) => setNewEmail(val)}
              />
              <Button
                variant='filled'
                shape='brick'
                widthFullSize
                loading={changeEmail.isPending}
                disabled={changeEmail.isPending || !password || !newEmail.includes('@')}
                onClick={handleChangeEmail}
              >
                Сохранить адрес и отправить письмо
              </Button>
            </div>
          ) : null}

          <div className={classes.loginPrompt}>
            <span style={{ color: '#666' }}>Уже есть аккаунт? </span>
            <NavLink to='/login' className={classes.link}>
              Войти
            </NavLink>
          </div>
        </div>
      </main>
    </section>
  );
};
